import { useSyncExternalStore } from 'react';
import { z } from 'zod';

const STORAGE_KEY = 'phoebe-theme';

const themeSchema = z.enum(['light', 'dark', 'system']);
export type Theme = z.infer<typeof themeSchema>;

// Default is light: the site never goes dark on its own — dark (or following
// the OS via 'system') is an explicit per-user opt-in from the toggle.
function parseTheme(value: unknown): Theme {
    const result = themeSchema.safeParse(value);
    return result.success ? result.data : 'light';
}

let currentTheme: Theme | null = null;
const listeners = new Set<() => void>();
let isWatchingEnvironment = false;

function readStoredTheme(): Theme {
    let storedTheme: string | null = null;
    try {
        storedTheme = localStorage.getItem(STORAGE_KEY);
    } catch {
        // localStorage unavailable (private browsing, etc.)
    }
    return parseTheme(storedTheme);
}

// matchMedia is absent in the jsdom test environment; treat that as light.
function systemPrefersDark(): boolean {
    return (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
    );
}

// The `dark` class drives the .dark token palette in global.css. index.html
// applies it before first paint with the same storage key + system fallback,
// so this only needs to keep the class in sync after hydration.
function applyThemeClass(theme: Theme) {
    const isDark =
        theme === 'dark' || (theme === 'system' && systemPrefersDark());
    document.documentElement.classList.toggle('dark', isDark);
}

function emitChange() {
    for (const listener of listeners) {
        listener();
    }
}

function ensureCurrentTheme(): Theme {
    if (currentTheme === null) {
        currentTheme = readStoredTheme();
        applyThemeClass(currentTheme);
    }
    if (!isWatchingEnvironment) {
        isWatchingEnvironment = true;
        // Follow OS appearance changes while in 'system'.
        if (typeof window.matchMedia === 'function') {
            window
                .matchMedia('(prefers-color-scheme: dark)')
                .addEventListener('change', () => {
                    if (currentTheme === 'system') {
                        applyThemeClass(currentTheme);
                        emitChange();
                    }
                });
        }
        // Follow theme changes made in another tab.
        window.addEventListener('storage', (event) => {
            if (event.key === STORAGE_KEY && event.newValue !== null) {
                const newTheme = parseTheme(event.newValue);
                if (newTheme !== currentTheme) {
                    currentTheme = newTheme;
                    applyThemeClass(newTheme);
                    emitChange();
                }
            }
        });
    }
    return currentTheme;
}

export const themeStore = {
    getTheme(): Theme {
        return ensureCurrentTheme();
    },
    setTheme(theme: Theme) {
        const previousTheme = ensureCurrentTheme();
        if (theme === previousTheme) {
            return;
        }
        currentTheme = theme;
        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch {
            // localStorage unavailable (private browsing, quota, etc.)
        }
        applyThemeClass(theme);
        emitChange();
    },
    // Re-applies the stored theme's class to <html>. Used by the admin-only
    // toggle gate after an external actor removed the class (e.g. the
    // forced-light enforcement) and the user becomes eligible again.
    refresh() {
        applyThemeClass(ensureCurrentTheme());
    },
    subscribe(listener: () => void) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
};

export function useTheme(): Theme {
    return useSyncExternalStore(themeStore.subscribe, themeStore.getTheme);
}
